import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { colorPrimary } from "../../Styles/Colors";

const ListScrollDialog = ({
  open = true,
  onClose,
  title = "Scrollable Dialog Title",
  description = "This dialog contains a scrollable list of items. You can scroll through the list to see all available options and select multiple items.",
  items = [
    { id: 1, text: "List Item 1", percentage: "100%", avatar: "A" },
    { id: 2, text: "List Item 2", percentage: "95%", avatar: "B" },
    { id: 3, text: "List Item 3", percentage: "87%", avatar: "C" },
    { id: 4, text: "List Item 4", percentage: "92%", avatar: "D" },
    { id: 5, text: "List Item 5", percentage: "78%", avatar: "E" },
    { id: 6, text: "List Item 6", percentage: "89%", avatar: "F" },
    { id: 7, text: "List Item 7", percentage: "96%", avatar: "G" },
    { id: 8, text: "List Item 8", percentage: "83%", avatar: "H" },
    { id: 9, text: "List Item 9", percentage: "91%", avatar: "I" },
    { id: 10, text: "List Item 10", percentage: "88%", avatar: "J" },
    { id: 11, text: "List Item 11", percentage: "94%", avatar: "K" },
    { id: 12, text: "List Item 12", percentage: "86%", avatar: "L" },
  ],
  primaryAction = "Action 1",
  secondaryAction = "Action 2",
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const [selectedItems, setSelectedItems] = useState(new Set());

  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction(Array.from(selectedItems));
    }
    if (onClose) onClose();
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction(Array.from(selectedItems));
    }
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          minHeight: 450,
          minWidth: 280,
          maxWidth: 560,
          maxHeight: "80vh",
          background: "var(--SMA-white, #FFF)",
          boxShadow:
            "0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.5rem",
          fontWeight: 500,
          color: colorPrimary,
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 0,
          pb: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <DialogContentText
          sx={{
            mb: 2,
            color: "text.secondary",
            lineHeight: 1.6,
            flexShrink: 0,
          }}
        >
          {description}
        </DialogContentText>

        <List
          sx={{
            border: "none",

            borderRadius: 2,
            bgcolor: "background.paper",
            overflow: "auto",
            maxHeight: 300,

            flex: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: colorPrimary,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {items.map((item, index) => (
            <ListItem
              key={item.id}
              button
              onClick={() => handleItemSelect(item.id)}
              selected={selectedItems.has(item.id)}
              divider={index !== items.length - 1}
              sx={{
                py: 1.5,
                px: 2,
                "&.Mui-selected": {
                  bgcolor: "primary.light",
                  "&:hover": {
                    bgcolor: "primary.light",
                  },
                },
                "&:hover": {
                  bgcolor: "action.hover",
                },
                transition: "background-color 0.2s ease",
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: colorPrimary,
                    width: 36,
                    height: 36,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {item.avatar || "A"}
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={item.text}
                sx={{
                  flex: 1,
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 400,
                  },
                }}
              />

              <Box
                sx={{
                  mr: 3,
                  minWidth: "fit-content",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                  }}
                >
                  {item.percentage}
                </Typography>
              </Box>

              <Checkbox
                checked={selectedItems.has(item.id)}
                onChange={() => handleItemSelect(item.id)}
                color="primary"
                size="small"
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: "24px",
                    color: colorPrimary,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 1,
          justifyContent: "flex-end",
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
        }}
      >
        <Button
          onClick={handleSecondaryAction}
          variant="outlined"
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            backgroundColor: "transparent",
            border: "none",
            color: colorPrimary,
          }}
        >
          {secondaryAction}
        </Button>
        <Button
          onClick={handlePrimaryAction}
          variant="contained"
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            backgroundColor: "transparent",
            border: "none",
            color: colorPrimary,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {primaryAction}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListScrollDialog;
